import { Color } from '@app/shared/models/color.model';
import { Invoice } from '@app/shared/interfaces/invoice.interface';
import * as z from 'zod';

export enum ValidationTypeEnum {
  REFUSED = 'REFUSED',
  GENERATED = 'GENERATED',
  VALIDATED = 'VALIDATED',
}

export enum InvoicesStatusEnum {
  EN_COURS = 'En cours',
  ARCHIVE = 'Archivées',
  ERROR = 'En erreur',
}

export enum InvoicesGetEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  SENT = 'SENT',
  ERROR = 'ERROR_SENDING',
}

export enum InvoicesPriceTypeEnum {
  ESTIME = 'E',
  REEL = 'R',
  DEFINITIF = 'D',
}

export enum InvoicesPriceTypeExportEnum {
  ESTIME = 'Estimé',
  REEL = 'Réel',
  DEFINITIF = 'Définitif',
}

export enum InvoicesTypeBillEnum {
  AUTOFACTURATION = 'Auto-facturation',
  DRAFT = 'Draft',
  AUTOAVOIR = 'Auto-avoir',
}

export const COLORS = {
  VALID_COLOR: Color.success500,
  INVALID_COLOR: Color.danger300,
  WARNING_COLOR: Color.secondaryLightBlue,
  DEFAULT_COLOR: Color.grey50,
};

/**
 * Mapping type bill
 * @param typeBill Le type bill
 * @private
 */
export function mapTypeBill(typeBill: string): string {
  switch (typeBill) {
    case 'AUTOFACTURATION':
      return InvoicesTypeBillEnum.AUTOFACTURATION;
    case 'DRAFT':
      return InvoicesTypeBillEnum.DRAFT;
    case 'AUTOAVOIR':
      return InvoicesTypeBillEnum.AUTOAVOIR;
    default:
      return 'Inconnu';
  }
}

/**
 * Mapping price type
 * @param priceType Le price type
 * @private
 */
export function mapPriceType(priceType: string): InvoicesPriceTypeEnum | string {
  switch (priceType) {
    case 'ESTIME':
      return InvoicesPriceTypeEnum.ESTIME;
    case 'REEL':
      return InvoicesPriceTypeEnum.REEL;
    case 'DEFINITIF':
      return InvoicesPriceTypeEnum.DEFINITIF;
    default:
      return '';
  }
}

/**
 * Mapping price type
 * @param priceType Le price type
 * @private
 */
export function mapPriceTypeForExport(priceType: string): InvoicesPriceTypeExportEnum | string {
  switch (priceType) {
    case 'ESTIME':
      return InvoicesPriceTypeExportEnum.ESTIME;
    case 'REEL':
      return InvoicesPriceTypeExportEnum.REEL;
    case 'DEFINITIF':
      return InvoicesPriceTypeExportEnum.DEFINITIF;
    default:
      return '';
  }
}

/**
 * Mapping price type to color
 * @param priceType Le price type
 * @private
 */
export function mapPriceTypeToColor(priceType: string): Color {
  switch (priceType) {
    case 'ESTIME':
      return COLORS.INVALID_COLOR;
    case 'REEL':
      return COLORS.VALID_COLOR;
    case 'DEFINITIF':
      return COLORS.WARNING_COLOR;
    default:
      return COLORS.DEFAULT_COLOR;
  }
}

export function mapInvoiceStatusToRequest(status: InvoicesStatusEnum): InvoicesGetEnum[] {
  const statusMap: Record<InvoicesStatusEnum, InvoicesGetEnum[]> = {
    [InvoicesStatusEnum.ERROR]: [InvoicesGetEnum.ERROR],
    [InvoicesStatusEnum.ARCHIVE]: [InvoicesGetEnum.SENT, InvoicesGetEnum.ERROR],
    [InvoicesStatusEnum.EN_COURS]: [InvoicesGetEnum.IN_PROGRESS, InvoicesGetEnum.ERROR],
  };

  return statusMap[status] ?? [InvoicesGetEnum.IN_PROGRESS, InvoicesGetEnum.ERROR];
}

export enum ErrorTypeFailedEmails {
  STREAMSERVE = 'STREAMSERVE',
  BREVO = 'BREVO',
  GENERIC = 'GENERIC',
}

export const mapTypeFailedEmailsToLibelle = (typeFailedEmails: string): string => {
  const map: Record<string, string> = {
    [ErrorTypeFailedEmails.STREAMSERVE]: 'Erreur Streamserve',
    [ErrorTypeFailedEmails.BREVO]: 'Erreur Brevo',
    [ErrorTypeFailedEmails.GENERIC]: 'Erreur générale',
  };

  return map[typeFailedEmails] || 'Erreur inconnue';
};

/**
 * Les factures avant sont en kWh
 * On convertit la quantité en MWh pour n'afficher que des MWh dans le tableau.
 * @param invoice La facture
 * @returns La facture avec la quantité exprimée en MWh
 */
export function normalizeQuantityToMWh(invoice: Invoice): Invoice {
  if (!invoice.quantity) return invoice;

  const isKwh = invoice.ArticleDetails?.some((article) => article.uniteQuantite === 'kWh');
  if (!isKwh) return invoice;

  return { ...invoice, quantity: (Number(invoice.quantity) / 1000).toString() };
}

const ProducerSchema = z.object({
  name: z.string(),
});

const ArticleSchema = z.object({
  montant: z.union([z.string(), z.number()]).optional(),
  typePrix: z.string().optional(),
  code: z.string().optional(),
});

const SiteSchema = z.object({
  name: z.string(),
  technology: z.string(),
});

const ContactSchema = z.object({
  email: z.string(),
});

const DeliveryStatusSchema = z.object({
  email: z.string(),
  status: z.string(),
  messageId: z.string().optional(),
  eventDate: z.string(),
  producerName: z.string().optional(),
});

const InvoiceSchema = z.object({
  documentId: z.string().optional(),
  prm: z.string(),
  allPrms: z.array(z.union([z.string(), z.number()])),
  comment: z.string().optional(),
  typeBill: z.string(),
  producer: ProducerSchema,
  contacts: z.array(ContactSchema),
  sentDate: z.string().optional(),
  site: SiteSchema,
  formula: z.string(),
  formulaLabel: z.string(),
  DateDebutCalcul: z.string(),
  DateFinCalcul: z.string(),
  RES_CT: ArticleSchema.optional(),
  RES_HNEG: ArticleSchema.optional(),
  tracking: z.string().optional(),
  ArticleDetails: z.array(ArticleSchema),
  status: z.string(),
  failedEmails: z.array(z.string()).optional(),
  deliveryStatuses: z.record(z.string(), DeliveryStatusSchema).optional(),
  quantity: z.string().optional(),
});

export const ListInvoiceSchema = z.object({
  Items: z.array(InvoiceSchema),
  Count: z.number().int(),
});
